import type { ItemCatalogEntry, Season } from '@cortex/domain-ac-mens-hub-api';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useItemCatalog } from '../hooks/useItemCatalog.js';

/**
 * 季節の表示順。phoenix-config constants.json の SUB_SEASON と同じ並び。
 * @graph-connects none
 */
const SEASON_ORDER: readonly Season[] = [
  'earlySpring',
  'lateSpring',
  'summer',
  'earlyAutumn',
  'lateAutumn',
  'winter',
] as const;

/** @graph-connects none */
const ALL = 'all';

/**
 * 日次同期された取得時刻を JST の分単位で表示する。
 * @graph-connects none
 */
export function formatSnapshotAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * カタログを画面側で絞り込む。母数が651件程度と小さいため全件をクライアントで保持し、
 * 入力のたびにサーバーへ問い合わせない。
 * @graph-connects none
 */
export function filterItems(
  items: ItemCatalogEntry[],
  filters: {
    category: string;
    season: string;
    keyword: string;
    availableOnly: boolean;
    withImageOnly: boolean;
  },
): ItemCatalogEntry[] {
  const keyword = filters.keyword.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.category !== ALL && item.categoryName !== filters.category) return false;
    if (filters.season !== ALL && !item.seasons.includes(filters.season as Season)) return false;
    if (filters.availableOnly && item.availableCount === 0) return false;
    if (filters.withImageOnly && item.frontImageUrl === null) return false;
    if (keyword !== '') {
      const haystack =
        `${item.itemName} ${item.brandName} ${item.colorName} ${item.fkuCode}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business アイテムカタログページ。エアクロメンズが保有する全アイテムをFKU（型×色）単位のカードで一覧し、カテゴリ・季節・キーワード・在庫状況で絞り込む。
 * @graph-connects useItemCatalog [calls] 保有アイテム一覧取得
 */
export function ItemCatalogPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useItemCatalog();

  const [category, setCategory] = useState<string>(ALL);
  const [season, setSeason] = useState<string>(ALL);
  const [keyword, setKeyword] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [withImageOnly, setWithImageOnly] = useState(false);

  const items = useMemo(() => data?.items ?? [], [data]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      counts.set(item.categoryName, (counts.get(item.categoryName) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  const summary = useMemo(
    () => ({
      typeCount: items.length,
      holdingTotal: items.reduce((sum, item) => sum + item.holdingCount, 0),
      availableTotal: items.reduce((sum, item) => sum + item.availableCount, 0),
      brandCount: new Set(items.map((item) => item.brandName)).size,
      withImageCount: items.filter((item) => item.frontImageUrl !== null).length,
    }),
    [items],
  );

  const filtered = useMemo(
    () => filterItems(items, { category, season, keyword, availableOnly, withImageOnly }),
    [items, category, season, keyword, availableOnly, withImageOnly],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {t('itemCatalog.title')}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">
          {t('itemCatalog.snapshotAt', { at: formatSnapshotAt(data?.snapshotAt ?? '') })}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-border)] md:grid-cols-5">
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">
            {t('itemCatalog.summary.types')}
          </dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {summary.typeCount.toLocaleString()}
          </dd>
        </div>
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">
            {t('itemCatalog.summary.holding')}
          </dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {summary.holdingTotal.toLocaleString()}
          </dd>
        </div>
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">
            {t('itemCatalog.summary.available')}
          </dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {summary.availableTotal.toLocaleString()}
          </dd>
        </div>
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">
            {t('itemCatalog.summary.brands')}
          </dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {summary.brandCount.toLocaleString()}
          </dd>
        </div>
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">
            {t('itemCatalog.summary.withImage')}
          </dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {summary.withImageCount.toLocaleString()}
            <span className="ml-1 text-[12px] font-normal text-[var(--color-text-muted)]">
              / {summary.typeCount.toLocaleString()}
            </span>
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
        <select
          aria-label={t('itemCatalog.filter.category')}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[13px] text-[var(--color-text-primary)]"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
        >
          <option value={ALL}>{t('itemCatalog.filter.categoryAll')}</option>
          {categories.map(([name, count]) => (
            <option key={name} value={name}>
              {name}（{count}）
            </option>
          ))}
        </select>

        <select
          aria-label={t('itemCatalog.filter.season')}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[13px] text-[var(--color-text-primary)]"
          value={season}
          onChange={(e) => {
            setSeason(e.target.value);
          }}
        >
          <option value={ALL}>{t('itemCatalog.filter.seasonAll')}</option>
          {SEASON_ORDER.map((key) => (
            <option key={key} value={key}>
              {t(`itemCatalog.season.${key}`)}
            </option>
          ))}
        </select>

        <input
          type="search"
          aria-label={t('itemCatalog.filter.keyword')}
          placeholder={t('itemCatalog.filter.keywordPlaceholder')}
          className="min-w-[200px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[13px] text-[var(--color-text-primary)]"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
          }}
        />

        <label className="flex items-center gap-1 text-[13px] text-[var(--color-text-primary)]">
          <input
            type="checkbox"
            checked={availableOnly}
            onChange={(e) => {
              setAvailableOnly(e.target.checked);
            }}
          />
          {t('itemCatalog.filter.availableOnly')}
        </label>

        <label className="flex items-center gap-1 text-[13px] text-[var(--color-text-primary)]">
          <input
            type="checkbox"
            checked={withImageOnly}
            onChange={(e) => {
              setWithImageOnly(e.target.checked);
            }}
          />
          {t('itemCatalog.filter.withImageOnly')}
        </label>

        <span className="ml-auto text-[13px] tabular-nums text-[var(--color-text-muted)]">
          {t('itemCatalog.filter.result', { count: filtered.length })}
        </span>
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center text-[13px] text-[var(--color-text-muted)]">
          {t('itemCatalog.empty')}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
          {filtered.map((item) => (
            <li
              key={item.fkuCode}
              className="flex flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]"
            >
              <div className="flex aspect-[3/4] items-center justify-center bg-[var(--color-surface-muted)]">
                {item.frontImageUrl === null ? (
                  <span className="px-2 text-center text-[12px] text-[var(--color-text-muted)]">
                    {t('itemCatalog.noImage')}
                  </span>
                ) : (
                  <img
                    src={item.frontImageUrl}
                    alt={item.itemName}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col gap-1 p-3">
                <span className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  {item.brandName}
                </span>
                <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">
                  {item.itemName}
                </span>
                <span className="text-[12px] text-[var(--color-text-muted)]">
                  {item.colorName}
                  {item.sizes.length > 0 ? ` ・ ${item.sizes.join(' / ')}` : ''}
                </span>
                <div className="mt-auto flex items-baseline justify-between gap-2 border-t border-[var(--color-border)] pt-2">
                  <span className="text-[12px] tabular-nums text-[var(--color-text-primary)]">
                    {t('itemCatalog.stock', {
                      holding: item.holdingCount,
                      available: item.availableCount,
                    })}
                  </span>
                  <span className="flex gap-[3px]" aria-hidden="true">
                    {SEASON_ORDER.map((key) => (
                      <i
                        key={key}
                        className={`h-[7px] w-[7px] rounded-full ${
                          item.seasons.includes(key)
                            ? 'bg-[var(--color-text-primary)]'
                            : 'bg-[var(--color-border)]'
                        }`}
                      />
                    ))}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
